import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'react-toastify';
import { API_URL } from './constants';
import { motion } from 'framer-motion';
import { FaSave, FaSearch, FaPlus, FaTrash } from 'react-icons/fa';

const CollaborativeGrid = ({ user, initialData, onDataChange }) => {
  const [gridApi, setGridApi] = useState(null);
  const [rowData, setRowData] = useState(initialData || []);
  const [originalData, setOriginalData] = useState(initialData || []);
  const [isEditing, setIsEditing] = useState(false);
  const [columnDefs, setColumnDefs] = useState([]);
  const [searchText, setSearchText] = useState('');
  const gridRef = useRef(null);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      const firstRow = initialData[0];
      const hasRowId = Object.keys(firstRow).some(c => c.toLowerCase() === 'rowid' || c.toLowerCase() === 'rowids' || c === '_rowId');
      const columns = [];
      if (!hasRowId) {
        columns.push({
          headerName: 'Row ID',
          valueGetter: 'node.rowIndex + 1',
          width: 75,
          pinned: 'left',
          sortable: true,
          filter: true,
          resizable: true,
          editable: false,
          headerClass: 'font-semibold text-gray-800',
        });
      }
      columns.push(...Object.keys(firstRow).map(key => ({
        field: key,
        headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
        sortable: true,
        filter: true,
        resizable: true,
        editable: true,
        flex: 1,
        minWidth: 100,
        wrapText: true,
        autoHeight: true,
        cellClass: 'text-gray-700',
        headerClass: 'font-semibold text-gray-800',
      })));
      columns.push({
        headerName: '',
        field: 'actions',
        width: 40,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (params) => {
          return (
            <button
              onClick={() => handleDeleteRow(params.node.rowIndex)}
              className="text-red-500 hover:text-red-700 transition-colors"
            >
              <FaTrash className="w-3 h-3" />
            </button>
          );
        }
      });
      setColumnDefs(columns);
      setRowData(initialData);
      setOriginalData(initialData);
    }
  }, [initialData]);

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: true,
    flex: 1,
    minWidth: 100,
    wrapText: true,
    autoHeight: true,
    cellClass: 'text-gray-700',
    headerClass: 'font-semibold text-gray-800',
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    gridRef.current = params.api;
  };

  const handleCellValueChanged = (event) => {
    setIsEditing(true);
    const newData = [...rowData];
    const rowIndex = newData.findIndex(row => row === event.data);
    if (rowIndex !== -1) {
      newData[rowIndex] = { ...event.data };
      setRowData(newData);
      if (onDataChange) {
        onDataChange(newData);
      }
    }
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchText(searchValue);
    if (gridApi) {
      gridApi.setQuickFilter(searchValue);
    }
  };

  // const handleAddRow = () => {
  //   if (rowData.length > 0) {
  //     const newRow = {};
  //     Object.keys(rowData[0]).forEach(key => {
  //       newRow[key] = '';
  //     });
  //     const newData = [newRow, ...rowData];
  //     setRowData(newData);
  //     if (onDataChange) {
  //       onDataChange(newData);
  //     }
  //     // Scroll to the top
  //     setTimeout(() => {
  //       if (gridApi) {
  //         gridApi.ensureIndexVisible(0);
  //       }
  //     }, 0);
  //   }
  // };
  const handleAddRow = () => {
    const newRow = {
      id: `new-${Date.now()}`, // Unique ID
    };
    Object.keys(rowData[0]).forEach(key => {
      if (key !== 'Id') newRow[key] = '';
    });
    const newData = [newRow, ...rowData];
    setRowData(newData);
    if (onDataChange) {
      onDataChange(newData);
    }
    setTimeout(() => {
      if (gridApi) {
        gridApi.ensureIndexVisible(0);
      }
    }, 0);
  };

  const handleDeleteRow = (rowIndex) => {
    const newData = rowData.filter((_, index) => index !== rowIndex);
    setRowData(newData);
    setIsEditing(true);
    if (onDataChange) {
      onDataChange(newData);
    }
  };

const getChanges = () => {
  const changes = {
    added: [],
    modified: [],
    deleted: [],
  };

  // Find added rows (they typically start with "new-" in the id)
  rowData.forEach(row => {
    const id = String(row.Id);
    if (id.startsWith('new-') || !originalData.some(origRow => String(origRow.Id) === id)) {
      changes.added.push(row);
    }
  });

  // Find modified rows (existing rows that have changed)
  rowData.forEach(currentRow => {
    const id = String(currentRow.Id);
    if (!id.startsWith('new-')) { // Skip new rows we've already handled
      const originalRow = originalData.find(row => String(row.Id) === id);
      if (originalRow) {
        // Check if any field has changed
        const hasChanged = Object.keys(currentRow).some(
          key => JSON.stringify(currentRow[key]) !== JSON.stringify(originalRow[key])
        );
        if (hasChanged) {
          changes.modified.push(currentRow);
        }
      }
    }
  });

  // Find deleted rows (rows in original data but not in current data)
  originalData.forEach(originalRow => {
    const id = String(originalRow.id);
    if (!rowData.some(row => String(row.Id) === id)) {
      changes.deleted.push(originalRow);
    }
  });

  console.log("Changes:", changes);
  return changes;
};

  // const getChanges = () => {
  //   const changes = {
  //     added: [],
  //     modified: [],
  //     deleted: []
  //   };

  //   // Find added and modified rows
  //   rowData.forEach((currentRow, index) => {
  //     // Check if this is a new row (all empty values)
  //     const isEmptyRow = Object.values(currentRow).every(value => value === '');
  //     if (isEmptyRow) {
  //       changes.added.push(currentRow);
  //       return;
  //     }

  //     // Check for modified rows
  //     const originalRow = originalData.find(row => 
  //       Object.keys(row).every(key => row[key] === currentRow[key])
  //     );
  //     if (!originalRow) {
  //       changes.modified.push(currentRow);
  //     }
  //   });

  //   // Find deleted rows
  //   originalData.forEach(originalRow => {
  //     const exists = rowData.some(currentRow => 
  //       Object.keys(originalRow).every(key => originalRow[key] === currentRow[key])
  //     );
  //     if (!exists) {
  //       changes.deleted.push(originalRow);
  //     }
  //   });
  //   console.log("Changes:")
  //   console.log(changes);
  //   return changes;
  // };

  const handleSave = async () => {
    try {
      const changes = getChanges();
      
      const response = await fetch(`${API_URL}/api/save-row-and-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rowData:changes,
          userId: user.userId,
          datasetName: "New"
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save data');
      }

      // Update original data after successful save
      setOriginalData(rowData);

      toast.success('Data saved successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setIsEditing(false);
    } catch (error) {
      toast.error('Error saving data: ' + error.message, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="bg-white p-4 rounded-t-lg shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSave}
              disabled={!isEditing}
              className={`flex items-center px-4 py-2 rounded-lg ${
                isEditing
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              } transition-colors duration-200`}
            >
              <FaSave className="mr-2" />
              Save Changes
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAddRow}
              className="flex items-center px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors duration-200"
            >
              <FaPlus className="mr-2" />
              Add Row
            </motion.button>
          </div>
          <div className="relative w-96">
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-grow">
        <div
          className="ag-theme-alpine h-full"
          style={{
            '--ag-header-background-color': '#f8fafc',
            '--ag-odd-row-background-color': '#ffffff',
            '--ag-row-hover-color': '#f0f7ff',
            '--ag-selected-row-background-color': '#e6f0ff',
            '--ag-border-color': '#e2e8f0',
            '--ag-font-family': 'Inter, system-ui, sans-serif',
          }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onCellValueChanged={handleCellValueChanged}
            enableCellTextSelection={true}
            suppressRowClickSelection={true}
            rowSelection="multiple"
            animateRows={true}
            pagination={true}
            paginationPageSize={10}
            onFirstDataRendered={(params) => {
              params.api.sizeColumnsToFit();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CollaborativeGrid; 